// Magnetisk deklinasjon via EKTE WMM (World Magnetic Model) — sfærisk-harmonisk
// modell med Gauss-koeffisienter og Schmidt-seminormaliserte Legendre-funksjoner.
// Dette er den ærlige erstatningen for den slettede dipol-tilnærmingen: her regnes
// hele feltvektoren (X/Y/Z), deklinasjon, inklinasjon og totalintensitet ut med
// samme algoritme som NOAA/NGA-referansekoden (GeomagnetismLibrary.c).
//
// Koeffisienttabellen under er WMM2020 (epoke 2020.0, gyldig 2020–2025), hentet
// ordrett fra NOAA sin offentlige/public-domain WMM.COF. Grad/orden 12.
//
// Verifisert mot NOAA sin publiserte testverdi (jf. magdec.test.ts):
//   lat=-80, lon=240, høyde=100 km, 2022.5 → D=68.55389°, I=-72.27367°,
//   X=5814.97, Y=14802.97, Z=-49755.31 nT.
// Ren TS, ingen avhengigheter, ingen nettverkskall.
/** WMM2020-koeffisienter (public domain, NOAA). Kolonner: n m g h gdot hdot.
 *  g/h i nT, gdot/hdot i nT/år. Epoke 2020.0. */
const WMM2020_COF = `
2020.0            WMM-2020
1 0 -29404.5 0.0 6.7 0.0
1 1 -1450.7 4652.9 7.7 -25.1
2 0 -2500.0 0.0 -11.5 0.0
2 1 2982.0 -2991.6 -7.1 -30.2
2 2 1676.8 -734.8 -2.2 -23.9
3 0 1363.9 0.0 2.8 0.0
3 1 -2381.0 -82.2 -6.2 5.7
3 2 1236.2 241.8 3.4 -1.0
3 3 525.7 -542.9 -12.2 1.1
4 0 903.1 0.0 -1.1 0.0
4 1 809.4 282.0 -1.6 0.2
4 2 86.2 -158.4 -6.0 6.9
4 3 -309.4 199.8 5.4 3.7
4 4 47.9 -350.1 -5.5 -5.6
5 0 -234.4 0.0 -0.3 0.0
5 1 363.1 47.7 0.6 0.1
5 2 187.8 208.4 -0.7 2.5
5 3 -140.7 -121.3 0.1 -0.9
5 4 -151.2 32.2 1.2 3.0
5 5 13.7 99.1 1.0 0.5
6 0 65.9 0.0 -0.6 0.0
6 1 65.6 -19.1 -0.4 0.1
6 2 73.0 25.0 0.5 -1.8
6 3 -121.5 52.7 1.4 -1.4
6 4 -36.2 -64.4 -1.4 0.9
6 5 13.5 9.0 -0.0 0.1
6 6 -64.7 68.1 0.8 1.0
7 0 80.6 0.0 -0.1 0.0
7 1 -76.8 -51.4 -0.3 0.5
7 2 -8.3 -16.8 -0.1 0.6
7 3 56.5 2.3 0.7 -0.7
7 4 15.8 23.5 0.2 -0.2
7 5 6.4 -2.2 -0.5 -1.2
7 6 -7.2 -27.2 -0.8 0.2
7 7 9.8 -1.9 1.0 0.3
8 0 23.6 0.0 -0.1 0.0
8 1 9.8 8.4 0.1 -0.3
8 2 -17.5 -15.3 -0.1 0.7
8 3 -0.4 12.8 0.5 -0.2
8 4 -21.1 -11.8 -0.1 0.5
8 5 15.3 14.9 0.4 -0.3
8 6 13.7 3.6 0.5 -0.5
8 7 -16.5 -6.9 0.0 0.4
8 8 -0.3 2.8 0.4 0.1
9 0 5.0 0.0 -0.1 0.0
9 1 8.2 -23.3 -0.2 -0.3
9 2 2.9 11.1 -0.0 0.2
9 3 -1.4 9.8 0.4 -0.4
9 4 -1.1 -5.1 -0.3 0.4
9 5 -13.3 -6.2 -0.0 0.1
9 6 1.1 7.8 0.3 -0.0
9 7 8.9 0.4 -0.0 -0.2
9 8 -9.3 -1.5 -0.0 0.5
9 9 -11.9 9.7 -0.4 0.2
10 0 -1.9 0.0 0.0 0.0
10 1 -6.2 3.4 -0.0 -0.0
10 2 -0.1 -0.2 -0.0 0.1
10 3 1.7 3.5 0.2 -0.3
10 4 -0.9 4.8 -0.1 0.1
10 5 0.6 -8.6 -0.2 -0.2
10 6 -0.9 -0.1 -0.0 0.1
10 7 1.9 -4.2 -0.1 -0.0
10 8 1.4 -3.4 -0.2 -0.1
10 9 -2.4 -0.1 -0.1 0.2
10 10 -3.9 -8.8 -0.0 -0.0
11 0 3.0 0.0 -0.0 0.0
11 1 -1.4 -0.0 -0.1 -0.0
11 2 -2.5 2.6 -0.0 0.1
11 3 2.4 -0.5 0.0 0.0
11 4 -0.9 -0.4 -0.0 0.2
11 5 0.3 0.6 -0.1 -0.0
11 6 -0.7 -0.2 0.0 0.0
11 7 -0.1 -1.7 -0.0 0.1
11 8 1.4 -1.6 -0.1 -0.0
11 9 -0.6 -3.0 -0.1 -0.1
11 10 0.2 -2.0 -0.1 0.0
11 11 3.1 -2.6 -0.1 -0.0
12 0 -2.0 0.0 0.0 0.0
12 1 -0.1 -1.2 -0.0 -0.0
12 2 0.5 0.5 -0.0 0.0
12 3 1.3 1.3 0.0 -0.1
12 4 -1.2 -1.8 -0.0 0.1
12 5 0.7 0.1 -0.0 -0.0
12 6 0.3 0.7 0.0 0.0
12 7 0.5 -0.1 -0.0 -0.0
12 8 -0.2 0.6 0.0 0.1
12 9 -0.5 0.2 -0.0 -0.0
12 10 0.1 -0.9 -0.0 -0.0
12 11 -1.1 -0.0 -0.0 0.0
12 12 -0.3 0.5 -0.1 -0.1
`;
const MAXORD = 12;
const EARTH_A = 6378.137; // WGS84 store halvakse (km)
const EARTH_B = 6356.7523142; // WGS84 lille halvakse (km)
const RE = 6371.2; // WMM geomagnetisk referanseradius (km)
const EPOCH = 2020.0;
/** Bygg en (MAXORD+1)×(MAXORD+1)-matrise fylt med 0. */
function mat() {
    const m = [];
    for (let i = 0; i <= MAXORD; i++)
        m.push(new Array(MAXORD + 1).fill(0));
    return m;
}
// Forhåndsberegn Schmidt-normaliserte (unnormaliserte) Gauss-koeffisienter c/cd
// og rekursjons-konstantene k én gang ved modul-last (samme som referanse-koden).
const C = mat(); // c[m][n] = g, c[n][m-1] = h
const CD = mat(); // cd tilsvarende for sekulær variasjon (dg/dt, dh/dt)
const K = mat();
(function initCoefficients() {
    // 1) Les rå Gauss-koeffisienter inn i c/cd.
    for (const line of WMM2020_COF.split('\n')) {
        const t = line.trim();
        if (!t || /[a-zA-Z]/.test(t))
            continue; // hopp over hode/tom
        const p = t.split(/\s+/).map(Number);
        if (p.length < 6)
            continue;
        const [n, m, gnm, hnm, dgnm, dhnm] = p;
        if (m > n || n > MAXORD)
            continue;
        C[m][n] = gnm;
        CD[m][n] = dgnm;
        if (m !== 0) {
            C[n][m - 1] = hnm;
            CD[n][m - 1] = dhnm;
        }
    }
    // 2) Konverter Schmidt-normaliserte koeffisienter → unnormaliserte, og fyll k.
    const snorm = mat();
    snorm[0][0] = 1;
    for (let n = 1; n <= MAXORD; n++) {
        snorm[0][n] = (snorm[0][n - 1] * (2 * n - 1)) / n;
        let j = 2;
        for (let m = 0, d = n + 1; d > 0; d--, m++) {
            K[m][n] = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3));
            if (m > 0) {
                const flnmj = ((n - m + 1) * j) / (n + m);
                snorm[m][n] = snorm[m - 1][n] * Math.sqrt(flnmj);
                j = 1;
                C[n][m - 1] *= snorm[m][n];
                CD[n][m - 1] *= snorm[m][n];
            }
            C[m][n] *= snorm[m][n];
            CD[m][n] *= snorm[m][n];
        }
    }
    K[1][1] = 0.0;
})();
/** Gjør en Date om til desimalår (UTC), til bruk med WMM-tidsjustering. */
export function decimalYear(date) {
    const year = date.getUTCFullYear();
    const leap = (year % 400 === 0) || (year % 4 === 0 && year % 100 !== 0);
    const msInYear = (365 + (leap ? 1 : 0)) * 86400_000;
    return year + (date.valueOf() - Date.UTC(year, 0)) / msInYear;
}
const DEG = Math.PI / 180;
/**
 * Beregn hele magnetfeltet i (latDeg, lonDeg) ved høyde `heightMeters` over
 * WGS84-ellipsoiden for gitt tidspunkt (desimalår, default = i dag).
 * Implementerer NOAA/NGA WMM-algoritmen: geodetisk→sfærisk, Legendre-rekursjon,
 * tidsjusterte Gauss-koeffisienter, og rotasjon tilbake til geodetiske akser.
 */
export function magneticField(latDeg, lonDeg, heightMeters = 0, time = decimalYear(new Date())) {
    const alt = heightMeters / 1000; // km over ellipsoide
    const dt = time - EPOCH;
    const a2 = EARTH_A * EARTH_A, b2 = EARTH_B * EARTH_B;
    const c2 = a2 - b2, a4 = a2 * a2, b4 = b2 * b2, c4 = a4 - b4;
    const rlat = latDeg * DEG, rlon = lonDeg * DEG;
    const srlat = Math.sin(rlat), crlat = Math.cos(rlat);
    const srlat2 = srlat * srlat, crlat2 = crlat * crlat;
    // Geodetisk → sfæriske (geosentriske) koordinater.
    const q = Math.sqrt(a2 - c2 * srlat2);
    const q1 = alt * q;
    const q2 = ((q1 + a2) / (q1 + b2)) ** 2;
    const ct = srlat / Math.sqrt(q2 * crlat2 + srlat2);
    const st = Math.sqrt(1.0 - ct * ct);
    const r = Math.sqrt(alt * alt + 2.0 * q1 + (a4 - c4 * srlat2) / (q * q));
    const d = Math.sqrt(a2 * crlat2 + b2 * srlat2);
    const ca = (alt + d) / r; // cos(sfærisk−geodetisk lat-differanse)
    const sa = (c2 * crlat * srlat) / (r * d);
    const sp = new Array(MAXORD + 1).fill(0);
    const cp = new Array(MAXORD + 1).fill(0);
    sp[0] = 0.0;
    cp[0] = 1.0;
    sp[1] = Math.sin(rlon);
    cp[1] = Math.cos(rlon);
    for (let m = 2; m <= MAXORD; m++) {
        sp[m] = sp[1] * cp[m - 1] + cp[1] * sp[m - 1];
        cp[m] = cp[1] * cp[m - 1] - sp[1] * sp[m - 1];
    }
    const p = mat(), dp = mat();
    p[0][0] = 1;
    dp[0][0] = 0;
    const pp = new Array(MAXORD + 1).fill(0);
    pp[0] = 1.0;
    const aor = RE / r;
    let ar = aor * aor;
    let br = 0.0, bt = 0.0, bp = 0.0, bpp = 0.0;
    for (let n = 1; n <= MAXORD; n++) {
        ar *= aor;
        for (let m = 0, dd = n + 1; dd > 0; dd--, m++) {
            // Unnormaliserte tilhørende Legendre-polynom + deriverte via rekursjon.
            if (n === m) {
                p[m][n] = st * p[m - 1][n - 1];
                dp[m][n] = st * dp[m - 1][n - 1] + ct * p[m - 1][n - 1];
            }
            else if (n === 1 && m === 0) {
                p[m][n] = ct * p[m][n - 1];
                dp[m][n] = ct * dp[m][n - 1] - st * p[m][n - 1];
            }
            else if (n > 1 && n !== m) {
                if (m > n - 2) {
                    p[m][n - 2] = 0;
                    dp[m][n - 2] = 0;
                }
                p[m][n] = ct * p[m][n - 1] - K[m][n] * p[m][n - 2];
                dp[m][n] = ct * dp[m][n - 1] - st * p[m][n - 1] - K[m][n] * dp[m][n - 2];
            }
            // Tidsjuster Gauss-koeffisientene (sekulær variasjon).
            const tcmn = C[m][n] + dt * CD[m][n];
            const tchn = m !== 0 ? C[n][m - 1] + dt * CD[n][m - 1] : 0;
            // Akkumuler ledd i den sfærisk-harmoniske utviklingen.
            const par = ar * p[m][n];
            let temp1, temp2;
            if (m === 0) {
                temp1 = tcmn * cp[m];
                temp2 = tcmn * sp[m];
            }
            else {
                temp1 = tcmn * cp[m] + tchn * sp[m];
                temp2 = tcmn * sp[m] - tchn * cp[m];
            }
            bt -= ar * temp1 * dp[m][n];
            bp += m * temp2 * par;
            br += (n + 1) * temp1 * par;
            // Spesialtilfelle: geografiske poler (st = 0).
            if (st === 0.0 && m === 1) {
                pp[n] = n === 1 ? pp[n - 1] : ct * pp[n - 1] - K[m][n] * pp[n - 2];
                bpp += m * temp2 * (ar * pp[n]);
            }
        }
    }
    bp = st === 0.0 ? bpp : bp / st;
    // Roter feltvektor fra sfæriske til geodetiske akser.
    const bx = -bt * ca - br * sa;
    const by = bp;
    const bz = bt * sa - br * ca;
    const bh = Math.sqrt(bx * bx + by * by);
    return {
        north: bx,
        east: by,
        down: bz,
        horizontalIntensity: bh,
        totalIntensity: Math.sqrt(bh * bh + bz * bz),
        declination: Math.atan2(by, bx) / DEG,
        inclination: Math.atan2(bz, bh) / DEG,
    };
}
/**
 * Bekvemsfunksjon: magnetisk deklinasjon (grader, øst positiv) i et punkt.
 * For Oslo (~59.9N, 10.7E) gir dette ~ +2–3° østlig, som forventet.
 */
export function magneticDeclination(latDeg, lonDeg, heightMeters = 0, time = decimalYear(new Date())) {
    return magneticField(latDeg, lonDeg, heightMeters, time).declination;
}
//# sourceMappingURL=magdec.js.map